<script setup>
import { ref, computed, onMounted } from "vue";
import AppDataTable from "@/components/AppDataTable.vue";
import { fetchWheelSummary, wheelExportUrl } from "./api";
import { toDate } from "@/lib/date";

// Backend shape: an array of monthly summaries, each { winners: [{key, value}], updated }.
// `winners` is a points-distribution histogram (key = points, value = number of
// winners who got that many points) for that month — NOT a per-user winner list.
const months = ref([]); // [{ label, winners, updated }]
const selectedIndex = ref(0);
const loading = ref(false);
const error = ref("");

const columns = [
  { key: "points",  label: "Points" },
  { key: "winners", label: "Number of Winners" },
];

const monthOptions = computed(() => months.value.map((m, i) => ({ title: m.label, value: i })));
const currentWinners = computed(() =>
  (months.value[selectedIndex.value]?.winners ?? []).map((w) => ({ points: w.key, winners: w.value }))
);

onMounted(async () => {
  loading.value = true;
  try {
    const data = await fetchWheelSummary();
    const fmt = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });
    months.value = data
      .map((m) => ({ winners: m.winners ?? [], updated: m.updated, _sortDate: toDate(m.updated)?.getTime() ?? 0 }))
      .sort((a, b) => a._sortDate - b._sortDate)
      .map((m) => ({ ...m, label: fmt.format(new Date(m._sortDate || Date.now())) }));
    selectedIndex.value = months.value.length ? months.value.length - 1 : 0;
  }
  catch { error.value = "Couldn't load wheel summary."; }
  finally { loading.value = false; }
});

function prevMonth() { if (selectedIndex.value > 0) selectedIndex.value--; }
function nextMonth() { if (selectedIndex.value < months.value.length - 1) selectedIndex.value++; }
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Wheel Summary{{ months[selectedIndex] ? ` — ${months[selectedIndex].label}` : "" }}</h1>
      <VBtn :href="wheelExportUrl()" target="_blank" variant="tonal">Export CSV</VBtn>
    </div>
    <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
    <div class="d-flex align-center gap-3 mb-4 flex-wrap">
      <VBtn variant="tonal" :disabled="selectedIndex <= 0" @click="prevMonth">&lt;&lt;&lt; Last Month</VBtn>
      <VSelect v-model="selectedIndex" :items="monthOptions" label="Month" density="compact" hide-details style="max-width:220px" />
      <VBtn variant="tonal" :disabled="selectedIndex >= months.length - 1" @click="nextMonth">Next Month &gt;&gt;&gt;</VBtn>
    </div>
    <AppDataTable :columns="columns" :rows="currentWinners" :loading="loading" row-key="points" empty-text="No wheel spins for this month." />
  </section>
</template>
